import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/AuthContext';
import AnonymityChoice from './AnonymityChoice';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { ArrowLeft, User } from 'lucide-react';

interface RevieweeInfo {
  id: string;
  displayName: string;
  email: string;
  photoURL?: string;
  role: string;
}

interface PeerReviewTemplate {
  id: string;
  name: string;
  title: string;
  description?: string;
  sections: Array<{
    id: string;
    title: string;
    description?: string;
    order: number;
    questions: Array<{
      id: string;
      type: string;
      title: string;
      description?: string;
      placeholder?: string;
      required?: boolean;
      options?: Array<{
        id: string;
        label: string;
        value: string;
      }>;
      order: number;
    }>;
  }>;
  settings: {
    allowAnonymous: boolean;
    forceAnonymous: boolean;
    requireAllQuestions: boolean;
    showProgressBar: boolean;
    thankYouMessage?: string;
  };
}

const PeerReview: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [reviewee, setReviewee] = useState<RevieweeInfo | null>(null);
  const [template, setTemplate] = useState<PeerReviewTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAnonymityChoice, setShowAnonymityChoice] = useState(true);
  const [isAnonymous, setIsAnonymous] = useState<boolean | null>(null);

  useEffect(() => {
    if (userId) {
      fetchRevieweeAndTemplate();
    }
  }, [userId]);

  const fetchRevieweeAndTemplate = async () => {
    try {
      setLoading(true);
      
      // Fetch reviewee information
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      const userRef = doc(db, 'users', userId!);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        setError('User not found');
        return;
      }
      
      const userData = userDoc.data();
      setReviewee({
        id: userDoc.id,
        displayName: userData.displayName || userData.email?.split('@')[0] || 'Unknown',
        email: userData.email || '',
        photoURL: userData.photoURL,
        role: userData.role || 'employee'
      });
      
      // Fetch active peer review template
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      const templatesRef = collection(db, 'peerReviewTemplates');
      const activeTemplateQuery = query(templatesRef, where('isActive', '==', true));
      const templateSnapshot = await getDocs(activeTemplateQuery);
      
      if (templateSnapshot.empty) {
        console.log('No active peer review template found');
        setError('No active peer review template found. Please contact an administrator to create and activate a template.');
        return;
      }
      
      const templateData = templateSnapshot.docs[0].data();
      console.log('Loaded template:', templateData);
      console.log('Template settings:', templateData.settings);
      setTemplate({
        id: templateSnapshot.docs[0].id,
        ...templateData
      } as PeerReviewTemplate);
      
    } catch (err: any) {
      console.error('Error fetching review data:', err);
      setError('Failed to load review information');
    } finally {
      setLoading(false);
    }
  };

  const handleAnonymityChoice = (anonymous: boolean) => {
    setIsAnonymous(anonymous);
    setShowAnonymityChoice(false);
    // Navigate to the form with the anonymity choice
    navigate(`/review/${userId}/form`, { 
      state: { isAnonymous: anonymous } 
    });
  };

  // Determine if we should show anonymity choice based on template settings
  const shouldShowAnonymityChoice = () => {
    if (!template) return false;
    
    console.log('Checking anonymity choice:', {
      allowAnonymous: template.settings?.allowAnonymous,
      forceAnonymous: template.settings?.forceAnonymous
    });
    
    // If anonymous feedback is not allowed, skip choice (always named)
    if (!template.settings?.allowAnonymous) {
      console.log('Anonymity not allowed, skipping choice');
      return false;
    }
    
    // If forced anonymous, skip choice (always anonymous)
    if (template.settings?.forceAnonymous) {
      console.log('Anonymity forced, skipping choice');
      return false;
    }
    
    // Otherwise, show the choice
    console.log('Showing anonymity choice');
    return true;
  };

  // Set anonymity based on template settings if no choice is needed
  useEffect(() => {
    if (template && !shouldShowAnonymityChoice()) {
      const anonymousMode = template.settings?.forceAnonymous || false;
      setIsAnonymous(anonymousMode);
      setShowAnonymityChoice(false);
      
      // Automatically navigate to the form since no choice is needed
      navigate(`/review/${userId}/form`, { 
        state: { isAnonymous: anonymousMode } 
      });
    }
  }, [template, userId, navigate]);

  const handleStartReview = () => {
    // Navigate to the review form with anonymity preference
    navigate(`/review/${userId}/form`, { 
      state: { isAnonymous } 
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading review...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="text-center py-8">
            <div className="text-destructive mb-4">
              <User className="h-16 w-16 mx-auto mb-4 opacity-50" />
            </div>
            <h3 className="text-lg font-medium mb-2">Unable to Load Review</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => navigate('/dashboard')} variant="outline">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!reviewee || !template) {
    return null;
  }

  // Show anonymity choice first (only if template allows choice)
  if (showAnonymityChoice && shouldShowAnonymityChoice()) {
    return (
      <AnonymityChoice
        revieweeName={reviewee.displayName}
        templateDescription={template.description}
        onContinue={handleAnonymityChoice}
        onCancel={() => navigate('/dashboard')}
      />
    );
  }

  // Show review form (placeholder for now)
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/dashboard')} className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-primary">{template.title}</h1>
              <p className="text-muted-foreground">
                Reviewing: {reviewee.displayName} • 
                {isAnonymous ? ' Anonymous feedback' : ' Named feedback'}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Review Form Coming Soon</CardTitle>
            <CardDescription>
              The peer review questionnaire will be implemented here
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 border rounded-lg">
                {reviewee.photoURL ? (
                  <img 
                    src={reviewee.photoURL} 
                    alt={reviewee.displayName} 
                    className="w-12 h-12 rounded-full object-cover" 
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-primary font-semibold text-lg">
                      {reviewee.displayName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div>
                  <p className="font-medium">{reviewee.displayName}</p>
                  <p className="text-sm text-muted-foreground">{reviewee.email}</p>
                  <p className="text-xs text-muted-foreground capitalize">{reviewee.role}</p>
                </div>
              </div>
              
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm">
                  <strong>Template:</strong> {template.name}
                </p>
                <p className="text-sm">
                  <strong>Sections:</strong> {template.sections.length}
                </p>
                <p className="text-sm">
                  <strong>Total Questions:</strong> {template.sections.reduce((total, section) => total + section.questions.length, 0)}
                </p>
                <p className="text-sm">
                  <strong>Feedback Mode:</strong> {isAnonymous ? 'Anonymous' : 'Named'}
                </p>
              </div>
              
              <Button onClick={handleStartReview} className="w-full">
                Start Review (Placeholder)
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default PeerReview;
